import { defineStore } from 'pinia'
import api from '@/api/api'
import type {
    Gcb2ChatListType,
    Gcb2ChatStatsItem,
    Gcb2OmnichannelChat,
    Gcb2OmnichannelMessage,
    Gcb2OmnichannelRealtimeSnapshotResponse,
    MessengerChat,
    MessengerMessage,
    ExtendedChat,
    ChatUnreadType,
} from '@/api/messenger/types'
import { parsePhoneNumber } from 'libphonenumber-js/min'
import { mediaFromMessagePayload, normalizeUploadedMedia } from '@/helpers/gcb2/dhChatMedia'

interface MessageCursor {
    beforeMessageId: string
    beforeCreatedAt: string
}

export interface ChatListFilters {
    search?: string
    type?: Gcb2ChatListType
    connectorId?: string
    connectorIds?: string[]
    types?: string[]
    clientIds?: string[]
    unread?: boolean
}

interface MessengerStoreState {
    chats: ExtendedChat[] | null
    messages: MessengerMessage[]
    fetchedMessagesChats: Set<string>
    messageCursors: Record<string, MessageCursor | null>
    messageFilters: Record<string, { search?: string; eventTypes?: string[] }>
    loading: boolean
    chatLoading: boolean
    currentChatId: string | null
    currentCommunication: any | null
    projectId: string | null
    currentActivationId: string | null
    omnichannel: boolean
    projectUnreadCount: number
    unreadChatsCount: number
    chatFeatureAvailable: boolean
    listFilters: ChatListFilters
    chatStats: Gcb2ChatStatsItem[] | null
}

export interface DhChatMessageUpsertedEvent {
    eventId: string
    projectId: string
    chatId: string
    messageId: string
    queueJobId?: string
    direction: 'incoming' | 'outgoing'
    occurredAt: string
    /** В архиве ли тред на момент события. У входящего всегда false. */
    archived?: boolean
}

export interface DhChatMessageRemovedEvent {
    eventId: string
    projectId: string
    chatId: string
    messageId: string
    occurredAt: string
}

function hoursPassed(date: Date, hours: number): boolean {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    return diff > hours * 60 * 60 * 1000
}

const getChatUnreadType = (chat: MessengerChat): ChatUnreadType => {
    if (!chat.lastMessageId) {
        return null
    }
    if (chat.lastMessageType === 'outgoing') {
        return null
    }
    if (hoursPassed(new Date(chat.lastMessageTimestamp), 1)) {
        return 'red'
    }
    if (!hoursPassed(new Date(chat.lastMessageTimestamp), 1)) {
        return 'yellow'
    }
    return null
}

const formatPhone = (rawPhone: string | null | undefined) => {
    if (!rawPhone) {
        return { phone: '', formattedPhone: '' }
    }
    const phone = rawPhone.startsWith('+') ? rawPhone : `+${rawPhone}`
    try {
        const parsed = parsePhoneNumber(phone)
        return { phone, formattedPhone: parsed?.format('INTERNATIONAL') || phone }
    } catch {
        return { phone, formattedPhone: phone }
    }
}

const mapChat = (chat: MessengerChat): ExtendedChat => {
    const phone = `+${chat.id.split('@')[0]}`
    const { formattedPhone } = formatPhone(phone)
    const unreadType = getChatUnreadType(chat)
    return {
        ...chat,
        name: chat.name || formattedPhone,
        phone,
        formattedPhone,
        unreadType,
    }
}

const getOmnichannelUnreadType = (item: Gcb2OmnichannelChat): ChatUnreadType => {
    if (!item.hasUnread || item.lastMessageDirection !== 'in') {
        return null
    }
    if (!item.lastMessageAt) {
        return 'yellow'
    }
    return hoursPassed(new Date(item.lastMessageAt), 1) ? 'red' : 'yellow'
}

export const mapOmnichannelChat = (item: Gcb2OmnichannelChat): ExtendedChat => {
    const { phone, formattedPhone } = formatPhone(item.phone)
    const lastMessageType =
        item.lastMessageDirection === 'in'
            ? 'incoming'
            : item.lastMessageDirection === 'out'
            ? 'outgoing'
            : ''

    return {
        id: item.chatId,
        appId: item.provider,
        ownerPhone: '',
        name: item.clientName || formattedPhone || item.providerChatId,
        avatarUrl: '',
        lastMessageId: item.lastMessageId || '',
        lastMessageText: item.lastMessageText || '',
        lastMessageTimestamp: item.lastMessageAt || '',
        lastMessageType,
        createdAt: item.lastMessageAt || '',
        updatedAt: item.lastMessageAt || '',
        communication: null,
        phone,
        formattedPhone,
        unreadType: getOmnichannelUnreadType(item),
        clientId: item.clientId,
        clientComment: item.clientComment,
        provider: item.provider,
        connectorId: item.connectorId,
        connectorName: item.connectorName,
        channelIdentity: item.channelIdentity,
        providerChatId: item.providerChatId,
        unreadCount: item.unreadCount,
        hasUnread: item.hasUnread,
    }
}

export const mapOmnichannelMessage = (
    item: Gcb2OmnichannelMessage,
    chatId: string
): MessengerMessage => {
    const type = item.direction === 'in' ? 'incoming' : 'outgoing'
    const media = mediaFromMessagePayload(item)
    return {
        id: item.messageId,
        appId: item.provider,
        chatId,
        status: (item.status as MessengerMessage['status']) || 'pending',
        type,
        contentType: media ? 'media' : 'text',
        text: item.text,
        media,
        mediaUrl: media?.src,
        timestamp: item.createdAt,
        createdAt: item.createdAt,
        updatedAt: item.createdAt,
        dhUserId: item.sendByUserId || undefined,
        queueJobId: item.queueJobId || null,
    }
}

const mergeChatsById = (
    existing: ExtendedChat[] | null,
    incoming: ExtendedChat[]
): ExtendedChat[] => {
    const merged = existing ? existing.concat(incoming) : incoming
    const seen = new Set<string>()
    return merged.filter((chat) => {
        if (seen.has(chat.id)) return false
        seen.add(chat.id)
        return true
    })
}

const sortOmnichannelChats = (chats: ExtendedChat[]): ExtendedChat[] =>
    [...chats].sort((a, b) => {
        const aTime = new Date(a.lastMessageTimestamp || 0).getTime()
        const bTime = new Date(b.lastMessageTimestamp || 0).getTime()
        return bTime - aTime
    })

const isChatUnavailableError = (error: any) => {
    const message = String(error?.message || error?.error?.message || '')
    return (
        error?.code === 404 ||
        error?.error?.code === 404 ||
        message.includes('chat feature is not available')
    )
}

export const useMessengerStore = defineStore('messenger', {
    state: (): MessengerStoreState => ({
        chats: null,
        messages: [],
        fetchedMessagesChats: new Set(),
        messageCursors: {},
        messageFilters: {},
        loading: false,
        chatLoading: false,
        currentChatId: null,
        currentCommunication: null,
        projectId: null,
        currentActivationId: null,
        omnichannel: false,
        projectUnreadCount: 0,
        unreadChatsCount: 0,
        chatFeatureAvailable: true,
        listFilters: {},
        chatStats: null,
    }),

    getters: {
        sortedChats: (state) => {
            if (state.omnichannel) {
                return sortOmnichannelChats(state.chats || [])
            }
            return [...(state.chats || [])].sort((a, b) => {
                const aTime = new Date(a.lastMessageTimestamp || 0).getTime()
                const bTime = new Date(b.lastMessageTimestamp || 0).getTime()
                return bTime - aTime
            })
        },
        messagesByChat: (state) => (chatId: string) =>
            state.messages
                .filter((el) => el.chatId === chatId)
                .sort(
                    (a, b) =>
                        new Date(a.timestamp || 0).getTime() -
                        new Date(b.timestamp || 0).getTime()
                ),
        currentChat: (state) => {
            if (!state.currentChatId || !state.chats) return null
            return state.chats.find((c) => c.id === state.currentChatId) || null
        },
        hasMoreMessages: (state) => (chatId: string) => {
            if (!state.omnichannel) return true
            const cursor = state.messageCursors[chatId]
            if (cursor === undefined) return true
            return cursor !== null
        },
    },

    actions: {
        setProjectId(projectId: string) {
            this.projectId = projectId
        },

        setOmnichannel(enabled: boolean) {
            this.omnichannel = enabled
        },

        setListFilters(filters: ChatListFilters) {
            this.listFilters = { ...filters }
        },

        releaseCurrentChat(chatId: string) {
            if (this.currentChatId === chatId) {
                this.currentChatId = null
            }
        },

        clearCurrentChat() {
            this.currentChatId = null
        },

        async fetchProjectUnreadCount() {
            if (!this.projectId || !this.omnichannel) return
            const { data, error } = await api.messenger.chat.getUnreadCountOmnichannel({
                projectId: this.projectId,
                connectorIds: this.listFilters.connectorIds,
                types: this.listFilters.types,
            })
            if (!error && data) {
                this.projectUnreadCount = data.unreadCount
            }
        },

        async fetchUnreadChatsCount() {
            if (!this.projectId || !this.omnichannel) return
            const { data, error } = await api.messenger.chat.getUnreadChatsCountOmnichannel({
                projectId: this.projectId,
                connectorIds: this.listFilters.connectorIds,
                types: this.listFilters.types,
            })
            if (!error && data) {
                this.unreadChatsCount = data.unreadChatsCount
            }
        },

        async fetchChatStats(search?: string) {
            if (!this.projectId || !this.omnichannel) return null

            const { data, error } = await api.messenger.chat.getChatStatsOmnichannel({
                projectId: this.projectId,
                search: search || undefined,
            })
            if (error || !data) {
                return null
            }

            this.chatStats = data
            return data
        },

        async fetchChats(limit: number = 50, offset: number = 0) {
            if (!this.projectId) {
                return
            }

            this.loading = true
            let data, error

            if (this.omnichannel) {
                const listParams: Parameters<typeof api.messenger.chat.getAllChatsOmnichannel>[0] = {
                    limit,
                    offset,
                    projectId: this.projectId,
                }
                if (this.listFilters.search) {
                    listParams.search = this.listFilters.search
                }
                if (this.listFilters.type) {
                    listParams.type = this.listFilters.type
                }
                if (this.listFilters.connectorId) {
                    listParams.connectorId = this.listFilters.connectorId
                }
                if (this.listFilters.clientIds?.length) {
                    listParams.clientIds = this.listFilters.clientIds
                }
                if (this.listFilters.unread) {
                    listParams.unread = true
                }
                ;({ data, error } = await api.messenger.chat.getAllChatsOmnichannel(listParams))

                if (isChatUnavailableError(error)) {
                    this.chatFeatureAvailable = false
                    this.chats = []
                    this.loading = false
                    return
                }
                this.chatFeatureAvailable = true

                if (error || !data) {
                    this.loading = false
                    return
                }

                const mappedChats = data.map(mapOmnichannelChat)
                if (offset === 0) {
                    this.chats = sortOmnichannelChats(mappedChats)
                } else {
                    this.chats = sortOmnichannelChats(mergeChatsById(this.chats, mappedChats))
                }

                await Promise.all([this.fetchProjectUnreadCount(), this.fetchUnreadChatsCount()])
                this.loading = false
                return data
            }

            if (this.currentActivationId) {
                ({ data, error } = await api.messenger.chat.getChats({
                    limit,
                    offset,
                    activationId: this.currentActivationId,
                    projectId: this.projectId,
                }))
            } else {
                ({ data, error } = await api.messenger.chat.getAllChats({
                    limit,
                    offset,
                    projectId: this.projectId,
                }))
            }

            if (error || !data) {
                this.loading = false
                return
            }

            const mappedChats = data.map(mapChat) as ExtendedChat[]
            if (offset === 0) {
                this.chats = mappedChats
            } else {
                this.chats = mergeChatsById(this.chats, mappedChats)
            }

            this.loading = false
            return data
        },

        setActivationId(activationId: string | null) {
            this.currentActivationId = activationId
        },

        setMessageFilters(
            chatId: string,
            filters: { search?: string; eventTypes?: string[] }
        ) {
            this.messageFilters[chatId] = filters
        },

        clearMessagesForChat(chatId: string) {
            this.messages = this.messages.filter((m) => m.chatId !== chatId)
            this.fetchedMessagesChats.delete(chatId)
            delete this.messageCursors[chatId]
        },

        async fetchMessages(
            chatId: string,
            limit: number = 20,
            offset: number = 0,
            options?: { append?: boolean; filters?: { search?: string; eventTypes?: string[] } }
        ) {
            if (!this.projectId) {
                return []
            }

            if (this.omnichannel) {
                const append = options?.append ?? offset > 0
                const filters = options?.filters ?? this.messageFilters[chatId] ?? {}
                const cursor = append ? this.messageCursors[chatId] : undefined

                const params: Parameters<typeof api.messenger.chat.getByChatOmnichannel>[0] = {
                    projectId: this.projectId,
                    chatId,
                    limit,
                    ...filters,
                }
                if (cursor) {
                    params.beforeMessageId = cursor.beforeMessageId
                    params.beforeCreatedAt = cursor.beforeCreatedAt
                }

                const { data, error } = await api.messenger.chat.getByChatOmnichannel(params)

                if (error || !data) {
                    return []
                }

                const mapped = data.items.map((item) => mapOmnichannelMessage(item, chatId))

                if (!append) {
                    this.messages = this.messages.filter((m) => m.chatId !== chatId)
                }

                const existingIds = new Set(
                    this.messages.filter((m) => m.chatId === chatId).map((m) => m.id)
                )
                const newMessages = mapped.filter((m) => !existingIds.has(m.id))
                this.messages = this.messages.concat(newMessages)
                this.fetchedMessagesChats.add(chatId)
                this.messageCursors[chatId] = data.nextCursor

                if (data.chat) {
                    const mappedChat = mapOmnichannelChat({
                        chatId: data.chat.chatId,
                        clientId: data.chat.clientId,
                        phone: data.chat.phone,
                        clientName: data.chat.clientName,
                        clientComment: data.chat.clientComment,
                        lastMessageId: null,
                        lastMessageAt: null,
                        lastMessageText: null,
                        lastMessageDirection: null,
                        lastMessageStatus: null,
                        unreadCount: 0,
                        hasUnread: false,
                        provider: data.chat.provider,
                        connectorId: data.chat.connectorId,
                        connectorName: data.chat.connectorName,
                        channelIdentity: data.chat.channelIdentity,
                        providerChatId: data.chat.providerChatId,
                    })
                    if (this.chats) {
                        const idx = this.chats.findIndex((c) => c.id === chatId)
                        if (idx !== -1) {
                            const existing = this.chats[idx]
                            mappedChat.lastMessageId = existing.lastMessageId
                            mappedChat.lastMessageText = existing.lastMessageText
                            mappedChat.lastMessageTimestamp = existing.lastMessageTimestamp
                            mappedChat.lastMessageType = existing.lastMessageType
                            mappedChat.unreadType = existing.unreadType
                            mappedChat.hasUnread = existing.hasUnread
                            mappedChat.unreadCount = existing.unreadCount
                            this.chats.splice(idx, 1, mappedChat)
                        }
                    }
                }

                return mapped
            }

            const { data, error } = await api.messenger.message.getByChat({
                chatId,
                limit,
                offset,
                projectId: this.projectId,
            })

            if (error) {
                return []
            }

            if (data && data.length > 0) {
                this.fetchedMessagesChats.add(chatId)
                this.messages = this.messages.concat(data)
            }
            return data || []
        },

        async markChatAsRead(chatId: string, lastVisibleMessageId?: string) {
            if (!this.projectId || !this.omnichannel) return
            // Тред и телефон — не chatId. /chat/read ждёт UUID, иначе 400.
            if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(chatId)) return

            const existing = this.chats?.find((c) => c.id === chatId)
            if (
                !lastVisibleMessageId &&
                existing &&
                !existing.hasUnread &&
                !(existing.unreadCount || 0)
            ) {
                return
            }

            const { data, error } = await api.messenger.chat.markChatReadOmnichannel({
                projectId: this.projectId,
                chatId,
                lastVisibleMessageId,
            })

            if (error || !data) return

            if (this.chats) {
                if (this.listFilters.unread) {
                    this.chats = this.chats.filter((c) => c.id !== chatId)
                } else {
                    const idx = this.chats.findIndex((c) => c.id === chatId)
                    if (idx !== -1) {
                        const chat = { ...this.chats[idx], unreadType: null, hasUnread: false, unreadCount: 0 }
                        this.chats.splice(idx, 1, chat)
                    }
                }
            }
            await Promise.all([this.fetchProjectUnreadCount(), this.fetchUnreadChatsCount()])
        },

        mergeRealtimeSnapshot(snapshot: Gcb2OmnichannelRealtimeSnapshotResponse) {
            const mappedChat = mapOmnichannelChat(snapshot.chat)
            const mappedMessage = mapOmnichannelMessage(snapshot.message, snapshot.chat.chatId)
            const shouldKeepMessage =
                this.currentChatId === mappedChat.id ||
                this.fetchedMessagesChats.has(mappedChat.id)

            if (shouldKeepMessage) {
                let messageIndex = this.messages.findIndex((m) => m.id === mappedMessage.id)

                if (messageIndex === -1 && mappedMessage.queueJobId) {
                    messageIndex = this.messages.findIndex(
                        (m) =>
                            m.chatId === mappedMessage.chatId &&
                            m.queueJobId === mappedMessage.queueJobId
                    )
                }

                if (messageIndex === -1 && mappedMessage.type === 'outgoing') {
                    const messageTime = new Date(mappedMessage.timestamp).getTime()
                    messageIndex = this.messages.findIndex((m) => {
                        if (
                            m.chatId !== mappedMessage.chatId ||
                            m.type !== 'outgoing' ||
                            m.status !== 'pending' ||
                            m.text !== mappedMessage.text
                        ) {
                            return false
                        }
                        const currentTime = new Date(m.timestamp).getTime()
                        return Math.abs(messageTime - currentTime) < 5 * 60 * 1000
                    })
                }

                if (messageIndex === -1) {
                    this.messages.push(mappedMessage)
                } else {
                    this.messages.splice(messageIndex, 1, mappedMessage)
                }
            }

            const hasActiveListFilters = Boolean(
                this.listFilters.search ||
                    this.listFilters.type ||
                    this.listFilters.connectorId ||
                    this.listFilters.clientIds?.length ||
                    this.listFilters.unread
            )
            const matchesConnector =
                !this.listFilters.connectorId ||
                mappedChat.connectorId === this.listFilters.connectorId
            const shouldAddChatToList =
                !hasActiveListFilters ||
                (matchesConnector &&
                    !this.listFilters.search &&
                    !this.listFilters.type &&
                    !this.listFilters.clientIds?.length &&
                    (!this.listFilters.unread || mappedChat.hasUnread))
            if (this.chats) {
                const chatIndex = this.chats.findIndex((c) => c.id === mappedChat.id)
                if (chatIndex !== -1) {
                    this.chats.splice(chatIndex, 1, mappedChat)
                } else if (shouldAddChatToList) {
                    this.chats.unshift(mappedChat)
                }
                this.chats = sortOmnichannelChats(this.chats)
            } else if (shouldAddChatToList) {
                this.chats = [mappedChat]
            }

            this.projectUnreadCount = snapshot.projectUnreadCount
            if (snapshot.message.direction === 'in') {
                void this.fetchUnreadChatsCount()
            }
            void this.fetchChatStats(this.listFilters.search)

            return {
                message: mappedMessage,
                chat: mappedChat,
            }
        },

        async handleDhChatMessageUpserted(
            event: DhChatMessageUpsertedEvent,
            options?: { isChatVisible?: (chatId: string) => boolean }
        ) {
            if (!event.projectId || !event.chatId || !event.messageId) {
                return null
            }

            if (!this.projectId) {
                this.projectId = event.projectId
            }
            this.omnichannel = true

            const { data, error } = await api.messenger.chat.getRealtimeSnapshotOmnichannel({
                projectId: event.projectId,
                chatId: event.chatId,
                messageId: event.messageId,
            })

            if (error || !data) {
                // eslint-disable-next-line no-console
                console.warn('[dh-chat][realtime] snapshot fetch failed', {
                    projectId: event.projectId,
                    chatId: event.chatId,
                    messageId: event.messageId,
                    error,
                })
                return null
            }

            let merged
            try {
                merged = this.mergeRealtimeSnapshot(data)
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[dh-chat][realtime] failed to merge snapshot', {
                    projectId: event.projectId,
                    chatId: event.chatId,
                    messageId: event.messageId,
                    err,
                })
                return null
            }

            const isIncoming = data.message.direction === 'in'
            const isChatVisible = options?.isChatVisible?.(event.chatId) ?? false
            const isChatOpen = this.currentChatId === event.chatId && isChatVisible

            if (isChatOpen && isIncoming) {
                await this.markChatAsRead(event.chatId, event.messageId)
            }

            return {
                snapshot: data,
                ...merged,
                // Не показываем всплывашку для чата, который сейчас открыт на экране
                shouldNotify: isIncoming && !isChatOpen,
            }
        },

        async handleDhChatMessageRemoved(event: DhChatMessageRemovedEvent) {
            if (!event.projectId || !event.chatId || !event.messageId) {
                return null
            }

            if (!this.projectId) {
                this.projectId = event.projectId
            }
            if (this.projectId !== event.projectId) {
                return null
            }

            const removed = this.messages.some(
                (message) => message.id === event.messageId && message.chatId === event.chatId
            )
            this.messages = this.messages.filter(
                (message) => message.id !== event.messageId || message.chatId !== event.chatId
            )

            const shouldRefreshMessages =
                this.currentChatId === event.chatId || this.fetchedMessagesChats.has(event.chatId)
            const refreshes: Promise<unknown>[] = [
                this.fetchChats(Math.max(this.chats?.length ?? 0, 50), 0),
                this.fetchChatStats(this.listFilters.search),
            ]
            if (shouldRefreshMessages) {
                refreshes.push(
                    this.fetchMessages(event.chatId, 20, 0, {
                        append: false,
                        filters: this.messageFilters[event.chatId],
                    })
                )
            }
            await Promise.all(refreshes)

            return { removed }
        },

        async selectChat(chatId: string) {
            if (!this.projectId) {
                this.chatLoading = false
                return null
            }

            if (this.chatLoading && this.currentChatId === chatId) {
                return null
            }

            this.currentChatId = chatId
            this.fetchedMessagesChats.add(chatId)
            this.chatLoading = true

            try {
                if (this.omnichannel) {
                    const chat = this.chats?.find((c) => c.id === chatId) || null
                    await this.markChatAsRead(chatId)
                    this.chatLoading = false
                    return chat
                }

                const { data, error } = await api.messenger.chat.getChatById({
                    chatId,
                    projectId: this.projectId,
                })

                if (error) {
                    this.chatLoading = false
                    return null
                }

                const chatData = Array.isArray(data) ? data[0] : data

                if (chatData && chatData.id) {
                    const mappedChat = mapChat(chatData)
                    if (this.chats) {
                        const chatIndex = this.chats.findIndex((c) => c.id === chatId)
                        if (chatIndex !== -1) {
                            const existingChat = this.chats[chatIndex]
                            if (
                                existingChat &&
                                existingChat.name &&
                                existingChat.name !== existingChat.phone &&
                                existingChat.name !== existingChat.formattedPhone &&
                                (!mappedChat.name ||
                                    mappedChat.name === mappedChat.phone ||
                                    mappedChat.name === mappedChat.formattedPhone)
                            ) {
                                mappedChat.name = existingChat.name
                            }
                            this.chats.splice(chatIndex, 1, mappedChat)
                        } else {
                            this.chats.push(mappedChat)
                        }
                    }
                }

                this.chatLoading = false
                return chatData
            } catch {
                this.chatLoading = false
                return null
            }
        },

        async sendMessage(chatId: string, text: string, file?: File | File[], replyOn?: string) {
            if (!this.projectId) {
                return
            }

            if (this.omnichannel) {
                const files: File[] = Array.isArray(file) ? file.slice() : file ? [file] : []
                const chat = this.chats?.find((c) => c.id === chatId)
                if (!chat) {
                    throw new Error('Чат не найден')
                }
                if (!text.trim() && !files.length) return

                const now = new Date().toISOString()
                const optimisticId = `pending-${Date.now()}`
                const previewUrl = files[0] && (files[0].type.startsWith('image/') || files[0].type.startsWith('video/'))
                    ? URL.createObjectURL(files[0])
                    : null
                const optimistic: MessengerMessage = {
                    id: optimisticId,
                    appId: chat?.provider ?? '',
                    chatId,
                    status: 'pending',
                    type: 'outgoing',
                    contentType: files.length ? 'media' : 'text',
                    text,
                    localPreviewUrl: previewUrl,
                    media: files[0]
                        ? {
                            src: previewUrl || '',
                            mimeType: files[0].type,
                            originalName: files[0].name,
                        }
                        : null,
                    timestamp: now,
                    createdAt: now,
                    updatedAt: now,
                }
                this.messages.push(optimistic)

                let mediaPayload: { src: string; mimeType: string; originalName?: string } | undefined
                if (files[0]) {
                    const uploaded = await api.file.uploadMedia({ projectId: this.projectId, file: files[0] })
                    if (uploaded.error || !uploaded.data) {
                        const idx = this.messages.findIndex((m) => m.id === optimisticId)
                        if (idx !== -1) this.messages.splice(idx, 1, { ...optimistic, status: 'failed' })
                        throw uploaded.error || new Error('Не удалось загрузить файл')
                    }
                    const normalized = normalizeUploadedMedia(uploaded.data.url ?? uploaded.data, files[0])
                    if (!normalized) throw new Error('Не удалось загрузить файл')
                    mediaPayload = normalized
                    const idx = this.messages.findIndex((m) => m.id === optimisticId)
                    if (idx !== -1) {
                        this.messages.splice(idx, 1, {
                            ...this.messages[idx],
                            media: normalized,
                            mediaUrl: normalized.src,
                        })
                    }
                }

                const { data, error } = await api.messenger.chat.sendMessageOmnichannel({
                    projectId: this.projectId,
                    chatId,
                    text,
                    connectorId: chat?.connectorId,
                    media: mediaPayload,
                })

                if (error) {
                    const idx = this.messages.findIndex((m) => m.id === optimisticId)
                    if (idx !== -1) {
                        this.messages.splice(idx, 1, { ...optimistic, status: 'failed' })
                    }
                    throw error
                }

                if (data?.connectorId && chat) {
                    chat.connectorId = data.connectorId
                }
                if (data?.queueJobId) {
                    const idx = this.messages.findIndex((m) => m.id === optimisticId)
                    if (idx !== -1) {
                        this.messages.splice(idx, 1, {
                            ...this.messages[idx],
                            queueJobId: data.queueJobId,
                        })
                    }
                }

                for (const extra of files.slice(1)) {
                    const uploaded = await api.file.uploadMedia({ projectId: this.projectId, file: extra })
                    const normalized = uploaded.data
                        ? normalizeUploadedMedia(uploaded.data.url ?? uploaded.data, extra)
                        : null
                    if (!normalized) continue
                    await api.messenger.chat.sendMessageOmnichannel({
                        projectId: this.projectId,
                        chatId,
                        text: '',
                        connectorId: chat.connectorId,
                        media: normalized,
                    })
                }

                return data
            }

            const singleFile = Array.isArray(file) ? file[0] : file
            let result
            if (singleFile) {
                result = await api.messenger.message.sendWithFile({
                    chatId,
                    message: text,
                    file: singleFile,
                    replyOn,
                    projectId: this.projectId,
                })
            } else {
                result = await api.messenger.message.sendMessage({
                    chatId,
                    message: text,
                    replyOn,
                    projectId: this.projectId,
                })
            }

            const { data, error } = result

            if (error) {
                throw error
            }

            if (data) {
                this.messages.push(data)
            }

            return data
        },

        async retrySendMessage(messageId: string) {
            if (!this.projectId) {
                return
            }

            const idx = this.messages.findIndex((m) => m.id === messageId)
            if (idx === -1) {
                return
            }

            const message = this.messages[idx]
            if (message.type !== 'outgoing') {
                return
            }

            if (this.omnichannel) {
                const chat = this.chats?.find((c) => c.id === message.chatId)
                this.messages.splice(idx, 1, { ...message, status: 'pending' })

                const retryMessageId =
                    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
                        message.id
                    )
                        ? message.id
                        : undefined
                const { data, error } = await api.messenger.chat.sendMessageOmnichannel({
                    projectId: this.projectId,
                    chatId: message.chatId,
                    text: message.text,
                    connectorId: chat?.connectorId,
                    retryMessageId,
                })

                if (error) {
                    const failedIdx = this.messages.findIndex((m) => m.id === messageId)
                    if (failedIdx !== -1) {
                        this.messages.splice(failedIdx, 1, { ...message, status: 'failed' })
                    }
                    throw error
                }

                if (data?.connectorId && chat) {
                    chat.connectorId = data.connectorId
                }
                if (data?.queueJobId) {
                    const updatedIdx = this.messages.findIndex((m) => m.id === messageId)
                    if (updatedIdx !== -1) {
                        this.messages.splice(updatedIdx, 1, {
                            ...this.messages[updatedIdx],
                            queueJobId: data.queueJobId,
                        })
                    }
                }

                return data
            }

            this.messages.splice(idx, 1, { ...message, status: 'pending' })

            const { data, error } = await api.messenger.message.sendMessage({
                chatId: message.chatId,
                message: message.text,
                projectId: this.projectId,
            })

            if (error) {
                const failedIdx = this.messages.findIndex((m) => m.id === messageId)
                if (failedIdx !== -1) {
                    this.messages.splice(failedIdx, 1, { ...message, status: 'failed' })
                }
                throw error
            }

            if (data) {
                const updatedIdx = this.messages.findIndex((m) => m.id === messageId)
                if (updatedIdx !== -1) {
                    this.messages.splice(updatedIdx, 1, data)
                }
            }

            return data
        },

        handleMessageReceived(data: { message: MessengerMessage; chat: MessengerChat }) {
            const existingMessageIndex = this.messages.findIndex((el) => el.id === data.message.id)
            const shouldAddMessage =
                existingMessageIndex === -1 &&
                (this.fetchedMessagesChats.has(data.chat.id) ||
                    this.currentChatId === data.chat.id)

            if (shouldAddMessage) {
                this.messages.push(data.message)
            } else if (existingMessageIndex !== -1) {
                this.messages.splice(existingMessageIndex, 1, data.message)
            }

            const updatedChat = this.omnichannel
                ? mapOmnichannelChat(data.chat as unknown as Gcb2OmnichannelChat)
                : mapChat(data.chat)
            if (this.chats) {
                const chatIndex = this.chats.findIndex((c) => c.id === data.chat.id)
                if (chatIndex !== -1) {
                    this.chats.splice(chatIndex, 1, updatedChat)
                } else {
                    this.chats = [updatedChat, ...this.chats]
                }
            } else {
                this.chats = [updatedChat]
            }
        },

        handleAvatarLoaded(data: { chatId: string; avatarUrl: string }) {
            if (!this.chats) return

            const chatIndex = this.chats.findIndex((el) => el.id === data.chatId)
            if (chatIndex !== -1) {
                const chat = this.chats[chatIndex]
                chat.avatarUrl = data.avatarUrl
                this.chats.splice(chatIndex, 1, this.omnichannel ? chat : mapChat(chat))
            }
        },

        clear() {
            this.chats = []
            this.messages = []
            this.fetchedMessagesChats.clear()
            this.messageCursors = {}
            this.messageFilters = {}
            this.currentChatId = null
            this.currentCommunication = null
            this.projectUnreadCount = 0
            this.unreadChatsCount = 0
        },
    },
})